import React from 'react';
import { useApp } from '../../context/AppContext';
import { statusOptions, releaseStatusOptions } from '../../mock';
import { Apple, Youtube, Music, ExternalLink, CheckCircle2, Circle, Filter, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';

const FILTER_LABELS = {
  'all': null,
  'upcoming': 'Showing only upcoming (invited + confirmed, not yet recorded)',
  'pending-release': 'Showing only guests with unsigned release forms',
  'published': 'Showing only published episodes',
  'completed': 'Showing only completed (recorded, edited, or published)',
  'potential': 'Showing only potential guests',
  'no-email': 'Showing only guests with no email address — click a row to add one',
  'missing-links': 'Showing only recorded/published guests missing a platform link'
};

const Pill = ({ value, options }) => {
  const opt = options.find(o => o.value === value) || options[0];
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${opt.color}22`, color: opt.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: opt.color }} />
      {opt.label}
    </span>
  );
};

export default function GridView({ onSelect, selectedIds = [], onSelectionChange }) {
  const { filteredGuests, activeShow, statusFilter, setStatusFilter } = useApp();

  const allSelected = filteredGuests.length > 0 && filteredGuests.every(g => selectedIds.includes(g.id));
  const toggleAll = () => {
    if (allSelected) onSelectionChange?.([]);
    else onSelectionChange?.(filteredGuests.map(g => g.id));
  };
  const toggleOne = (id) => {
    if (selectedIds.includes(id)) onSelectionChange?.(selectedIds.filter(x => x !== id));
    else onSelectionChange?.([...selectedIds, id]);
  };

  const activeFilterLabel = FILTER_LABELS[statusFilter];

  return (
    <div className="overflow-auto">
      {activeFilterLabel && (
        <div data-testid="active-status-filter-banner" className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200 text-[12px] text-blue-800">
          <Filter className="w-3.5 h-3.5" />
          <span className="flex-1">{activeFilterLabel} · {filteredGuests.length} match{filteredGuests.length === 1 ? '' : 'es'}</span>
          <Button data-testid="clear-status-filter-btn" size="sm" variant="ghost" className="h-7 text-blue-700 hover:bg-blue-100" onClick={() => setStatusFilter('all')}>
            <X className="w-3 h-3 mr-1" /> Clear filter
          </Button>
        </div>
      )}
      <div className="md:hidden divide-y divide-stone-100" data-testid="guest-card-list">
        {filteredGuests.map(g => {
          const sel = selectedIds.includes(g.id);
          return (
            <div key={g.id} data-testid={`guest-card-${g.id}`} className={cn('flex items-start gap-3 px-3 py-3 active:bg-stone-50', sel && 'bg-amber-50/50')}>
              <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={sel} onCheckedChange={() => toggleOne(g.id)} />
              </div>
              <button type="button" className="flex-1 min-w-0 text-left flex items-start gap-3" onClick={() => onSelect(g.id)}>
                {g.avatar ? (
                  <img src={g.avatar} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full grid place-items-center text-white text-[12px] font-semibold shrink-0" style={{ background: activeShow.color }}>
                    {g.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-stone-900 truncate text-[14px]">{g.name}</div>
                    <span className="text-[11px] text-stone-500 shrink-0">{g.episodeDate || ''}</span>
                  </div>
                  <div className="text-[12px] text-stone-500 truncate">{g.episodeTitle || g.company || <span className="italic text-stone-400">untitled</span>}</div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <Pill value={g.status} options={statusOptions} />
                    {g.releaseStatus === 'signed'
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      : <Pill value={g.releaseStatus} options={releaseStatusOptions} />}
                    <span className="flex items-center gap-1 ml-auto">
                      {g.platforms?.apple && <Apple className="w-3.5 h-3.5 text-stone-700" />}
                      {g.platforms?.spotify && <Music className="w-3.5 h-3.5 text-emerald-600" />}
                      {g.platforms?.youtube && <Youtube className="w-3.5 h-3.5 text-red-600" />}
                    </span>
                  </div>
                  {g.email && <div className="text-[11.5px] text-stone-500 truncate mt-1">{g.email}</div>}
                </div>
              </button>
            </div>
          );
        })}
        {filteredGuests.length === 0 && <div className="px-4 py-10 text-center text-stone-400 text-[13px]">No guests yet</div>}
      </div>
      <table className="hidden md:table min-w-full text-[13px]">
        <thead className="sticky top-0 bg-stone-50 border-b border-stone-200 z-10">
          <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500">
            <th className="px-3 py-2.5 w-8">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
            </th>
            <th className="px-4 py-2.5 font-semibold w-[220px]">Guest</th>
            <th className="px-4 py-2.5 font-semibold w-[260px]">Episode</th>
            <th className="px-4 py-2.5 font-semibold">Air date</th>
            <th className="px-4 py-2.5 font-semibold">Status</th>
            <th className="px-4 py-2.5 font-semibold">Release</th>
            <th className="px-4 py-2.5 font-semibold">Platforms</th>
            <th className="px-4 py-2.5 font-semibold">Email</th>
          </tr>
        </thead>
        <tbody>
          {filteredGuests.map((g, i) => {
            const sel = selectedIds.includes(g.id);
            return (
            <tr key={g.id} className={cn('border-b border-stone-100 hover:bg-stone-50', i % 2 === 0 ? 'bg-white' : 'bg-stone-50/30', sel && 'bg-amber-50/40 hover:bg-amber-50/60')}>
              <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={sel} onCheckedChange={() => toggleOne(g.id)} />
              </td>
              <td className="px-4 py-2.5 cursor-pointer" onClick={() => onSelect(g.id)}>
                <div className="flex items-center gap-2.5">
                  {g.avatar ? (
                    <img src={g.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full grid place-items-center text-white text-[11px] font-semibold" style={{ background: activeShow.color }}>
                      {g.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-stone-900 truncate">{g.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">{g.company}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-2.5 text-stone-700 cursor-pointer" onClick={() => onSelect(g.id)}>
                <div className="truncate max-w-[260px]">{g.episodeTitle || <span className="text-stone-400 italic">untitled</span>}</div>
              </td>
              <td className="px-4 py-2.5 text-stone-600 cursor-pointer" onClick={() => onSelect(g.id)}>{g.episodeDate || '—'}</td>
              <td className="px-4 py-2.5 cursor-pointer" onClick={() => onSelect(g.id)}><Pill value={g.status} options={statusOptions} /></td>
              <td className="px-4 py-2.5 cursor-pointer" onClick={() => onSelect(g.id)}>
                <div className="flex items-center gap-1.5">
                  {g.releaseStatus === 'signed' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Circle className="w-3.5 h-3.5 text-stone-400" />}
                  <Pill value={g.releaseStatus} options={releaseStatusOptions} />
                </div>
              </td>
              <td className="px-4 py-2.5 cursor-pointer" onClick={() => onSelect(g.id)}>
                <div className="flex items-center gap-1.5">
                  {g.platforms?.apple && <Apple className="w-3.5 h-3.5 text-stone-700" />}
                  {g.platforms?.spotify && <Music className="w-3.5 h-3.5 text-emerald-600" />}
                  {g.platforms?.youtube && <Youtube className="w-3.5 h-3.5 text-red-600" />}
                  {g.platforms?.libsyn && <ExternalLink className="w-3.5 h-3.5 text-stone-500" />}
                  {!g.platforms?.apple && !g.platforms?.spotify && !g.platforms?.youtube && !g.platforms?.libsyn && <span className="text-stone-300 text-[11px]">—</span>}
                </div>
              </td>
              <td className="px-4 py-2.5 text-stone-600 text-[12px] truncate cursor-pointer" onClick={() => onSelect(g.id)}>{g.email}</td>
            </tr>
          )})}
          {filteredGuests.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-10 text-center text-stone-400">No guests yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
